/* eslint-disable react-refresh/only-export-components -- context exports shared hook and provider */
import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { setSentryUser } from '@/lib/sentry';
import * as Sentry from '@sentry/react';
import { logger as globalLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import { excludeDelegatedLocations } from '@/features/locations/utils/delegatedLocations';
import { STATION_ROLES } from '@/lib/roles';
import i18next from 'i18next';

const LOCATION_ID_STORAGE_KEY = 'threadheads-selected-location-id';
const LOCATION_NAME_STORAGE_KEY = 'threadheads-selected-location';
// Sentinel value stored when user deliberately selects "All Locations"
// so admin default-to-HQ doesn't override their choice
const ALL_LOCATIONS_SENTINEL = '__ALL_LOCATIONS__';
const ACTIVE_ORG_ID_STORAGE_KEY = 'activeOrgId';
const LEGACY_ORG_ID_STORAGE_KEY = 'selected_org_id';

export interface Location {
  id: string;
  shopify_location_id: number;
  name: string;
  country_code: string;
  timezone: string;
  city: string;
  is_active: boolean;
  /** Missing on legacy cached DTOs; absence is treated as enabled. */
  production_enabled?: boolean;
  enable_auto_availability: boolean;
  // BLU-367: 3PL shared-location provenance.
  // owner_organization_id matches the active org for owned locations; for
  // shared-access locations it carries the 3PL provider's org id.
  // is_shared is true only when the active org is a CLIENT of this location
  // (reached via active junction). Both default safely for legacy single-tenant.
  owner_organization_id?: string | null;
  // BLU-367 Phase E.14: human-readable owner org name when this is a shared
  // (3PL) location. null for owned locations.
  owner_organization_name?: string | null;
  is_shared?: boolean;
  // BLU-711 A1: on a shared (3PL) location, true when the owner has opted this
  // org in to adjusting its own stock there. Read-only signal — the backend
  // guard is the policy. Always false on owned locations, where the caller is
  // the owner and already has stock authority.
  client_stock_writes_enabled?: boolean;
  /**
   * BLU-879: set when this location's fulfilment is DELEGATED to a shared 3PL
   * location the org is an active client of. Carried so the one filter that
   * acts on it (`excludeDelegatedLocations`, applied here) has something to
   * read, and so `useLocations`' DTO and this one stop being two shapes.
   *
   * Do NOT branch on this field in a surface. There are three lists, one per
   * question: `locations` (below) is where future WORK is chosen; `allLocations`
   * (below) is what a record NAMES; and the stock-write pickers — where STOCK
   * physically sits, delegated or not — go through `StockWriteLocationItems`
   * instead of either. A third, ad-hoc rule per page is how the three drift
   * apart again.
   */
  delegated_to_location_id?: string | null;
  /**
   * BLU-1423: the client the Orders page opens on at this floor (null = All
   * clients). Owner-side config: always null on a shared location.
   */
  queue_default_client_org_id?: string | null;
}

interface LocationContextType {
  selectedLocation: Location | null;
  /**
   * True only when `selectedLocation` is the operator's own choice: picked in
   * the location picker this session, or their saved pick restored from an
   * earlier one. False for the automatic defaults (a station role's first
   * floor, an admin's "HQ" name match or first floor), for All Locations and
   * after an org switch. Anything that treats the selection as the operator
   * choosing a target (e.g. where artwork is published) must check this.
   */
  selectedByUser: boolean;
  setSelectedLocation: (location: Location | null) => void;
  /**
   * The SELECTABLE locations: delegated ("shadow") rows dropped. Use this
   * anywhere the operator is CHOOSING a location.
   */
  locations: Location[];
  /**
   * EVERY location, delegated rows included. Use this anywhere an id is being
   * resolved to a NAME — orders, transfers, shipments, logs and stock movements
   * all point at delegated locations, and resolving those against the
   * selectable list renders "Unknown location" on records that are perfectly
   * well known (BLU-879 review). Naming is not choosing.
   */
  allLocations: Location[];
  isLoading: boolean;
  /**
   * True only once a successful `locations.list` answer has been stored in
   * `locations`, and false again on loading, error, sign-out and org switch. An
   * empty `locations` means "this org has no floor" only while this is true; a
   * wiped or not-yet-loaded list is empty too (BLU-1335).
   */
  locationsListed: boolean;
}

const LocationContext = createContext<LocationContextType>({
  selectedLocation: null,
  selectedByUser: false,
  setSelectedLocation: () => {},
  locations: [],
  allLocations: [],
  isLoading: true,
  locationsListed: false,
});

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  // The location and whether the operator chose it (see `selectedByUser`).
  // Every automatic write passes no flag, so it can never read as a choice.
  const [selection, setSelection] = useState<{ location: Location | null; byUser: boolean }>({
    location: null,
    byUser: false,
  });
  const selectedLocation = selection.location;
  const setSelectedLocationState = useCallback(
    (location: Location | null, byUser = false) => setSelection({ location, byUser }),
    [],
  );
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  /**
   * Delegated rows dropped, memoised. `excludeDelegatedLocations` returns the
   * SAME array when nothing is delegated, so the common single-tenant case
   * hands consumers a stable reference (several hold it in effect deps).
   */
  const locations = useMemo(() => excludeDelegatedLocations(allLocations), [allLocations]);
  const [isLoading, setIsLoading] = useState(true);
  const [locationsListed, setLocationsListed] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_ORG_ID_STORAGE_KEY) || localStorage.getItem(LEGACY_ORG_ID_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const { user, profile, loading: authLoading } = useAuth();
  const locationsQuery = trpc.locations.list.useQuery(
    { includeInactive: false, organizationId: activeOrgId ?? undefined },
    {
      enabled: !!activeOrgId && !!user?.id && !!profile?.id && !authLoading,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 30_000,
    }
  );
  const currentLocationIdRef = useRef<string | null>(null);

  // Keep active org id in sync without waiting for OrganizationContext,
  // so location loading can start as soon as auth/profile is available.
  useEffect(() => {
    if (authLoading) return;

    let storedOrgId: string | null = null;
    try {
      storedOrgId =
        localStorage.getItem(ACTIVE_ORG_ID_STORAGE_KEY) || localStorage.getItem(LEGACY_ORG_ID_STORAGE_KEY);
    } catch {
      storedOrgId = null;
    }

    const resolvedOrgId = storedOrgId || profile?.organization_id || null;
    if (resolvedOrgId && resolvedOrgId !== activeOrgId) {
      setActiveOrgId(resolvedOrgId);
      try {
        localStorage.setItem(ACTIVE_ORG_ID_STORAGE_KEY, resolvedOrgId);
      } catch {
        // ignore
      }
      return;
    }

    if (!resolvedOrgId && activeOrgId !== null) {
      setActiveOrgId(null);
    }
  }, [authLoading, profile?.organization_id, activeOrgId]);

  // Helper function to update Sentry context with location
  // Deps use primitive values from profile to avoid re-creating when profile object reference changes
  const profileId = profile?.id;
  const profileEmail = profile?.email;
  const profileRole = profile?.role;
  const updateSentryLocationContext = useCallback((location: Location) => {
    if (!profileId) return;

    try {
      setSentryUser(profileId, location.id, location.name, profileEmail, profileRole);
      globalLogger.debug('Sentry context updated with location', {
        operation: 'update_sentry_context',
        locationId: location.id,
        locationName: location.name,
      });
    } catch (error) {
      // Non-critical - Sentry update failure doesn't affect functionality
      globalLogger.debug('Sentry context update failed', {
        operation: 'update_sentry_context',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [profileId, profileEmail, profileRole]);

  // Auto-select location based on localStorage or first available
  const autoSelectLocation = useCallback(async (availableLocations: Location[]) => {
    try {
      // Check localStorage first
      const savedLocationId = localStorage.getItem(LOCATION_ID_STORAGE_KEY);
      if (savedLocationId) {
        const savedLocation = availableLocations.find(loc => loc.id === savedLocationId);
        if (savedLocation) {
          if (currentLocationIdRef.current !== savedLocation.id) {
            globalLogger.debug('Using saved location from localStorage', {
              operation: 'auto_select_location',
              locationName: savedLocation.name,
              locationId: savedLocation.id,
            });
            currentLocationIdRef.current = savedLocation.id;
            setSelectedLocationState(savedLocation, true);
            updateSentryLocationContext(savedLocation);
          }
          return;
        } else {
          globalLogger.debug('Saved location id not in assigned locations', {
            operation: 'auto_select_location',
            savedLocationId,
          });
        }
      }

      const savedLocationName = localStorage.getItem(LOCATION_NAME_STORAGE_KEY);
      if (savedLocationName) {
        const savedLocation = availableLocations.find(loc => loc.name === savedLocationName);
        if (savedLocation) {
          if (currentLocationIdRef.current !== savedLocation.id) {
            globalLogger.debug('Using legacy saved location from localStorage', {
              operation: 'auto_select_location',
              locationName: savedLocation.name,
              locationId: savedLocation.id,
            });
            currentLocationIdRef.current = savedLocation.id;
            setSelectedLocationState(savedLocation, true);
            updateSentryLocationContext(savedLocation);
          }
          localStorage.setItem(LOCATION_ID_STORAGE_KEY, savedLocation.id);
          localStorage.setItem(LOCATION_NAME_STORAGE_KEY, savedLocation.name);
          return;
        } else {
          globalLogger.debug('Saved location name not in assigned locations', {
            operation: 'auto_select_location',
            savedLocationName,
          });
        }
      }

      // Check if user deliberately chose "All Locations" (sentinel in localStorage)
      const savedId = localStorage.getItem(LOCATION_ID_STORAGE_KEY);
      if (savedId === ALL_LOCATIONS_SENTINEL) {
        if (currentLocationIdRef.current !== null) {
          globalLogger.debug('Respecting deliberate All Locations choice', {
            operation: 'auto_select_location',
          });
          currentLocationIdRef.current = null;
          setSelectedLocationState(null);
        }
        return;
      }

      // Station users (STATION_ROLES: Picker/Printer/Catcher/Packer/DTF Printer) always
      // default to the first available location; without this a DTF Printer would
      // sit on All Locations and the film pages would load nothing.
      if (profileRole && STATION_ROLES.has(profileRole) && availableLocations.length > 0) {
        const defaultLocation = availableLocations[0];
        if (defaultLocation && currentLocationIdRef.current !== defaultLocation.id) {
          globalLogger.debug('Station user defaulting to first location', {
            operation: 'auto_select_location',
            role: profileRole,
            locationName: defaultLocation.name,
            locationId: defaultLocation.id,
          });
          currentLocationIdRef.current = defaultLocation.id;
          setSelectedLocationState(defaultLocation);
          updateSentryLocationContext(defaultLocation);
        }
        return;
      }

      // No saved location — if admin, default to HQ location instead of "All Locations"
      if (profileRole === 'Admin' && availableLocations.length > 0) {
        const hqLocation = availableLocations.find(
          (loc) => loc.name.toLowerCase().includes('hq') || loc.name.toLowerCase().includes('headquarters')
        );
        const defaultLocation = hqLocation ?? availableLocations[0];
        if (defaultLocation && currentLocationIdRef.current !== defaultLocation.id) {
          globalLogger.debug('Admin defaulting to HQ location', {
            operation: 'auto_select_location',
            locationName: defaultLocation.name,
            locationId: defaultLocation.id,
            isHqMatch: !!hqLocation,
          });
          currentLocationIdRef.current = defaultLocation.id;
          setSelectedLocationState(defaultLocation);
          updateSentryLocationContext(defaultLocation);
        }
        return;
      }

      // Non-admin or no locations — default to "All Locations" (null)
      if (currentLocationIdRef.current !== null) {
        globalLogger.debug('Defaulting to All Locations', {
          operation: 'auto_select_location',
        });
        currentLocationIdRef.current = null;
        setSelectedLocationState(null);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');

      // Capture auto-selection error in Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'LocationContext',
          operation: 'autoSelectLocation',
          userId: profileId,
        },
        extra: {
          availableLocations: availableLocations.length,
          savedLocationId: localStorage.getItem(LOCATION_ID_STORAGE_KEY),
          error: err.message,
        },
        level: 'warning', // Lower severity as we have a fallback
      });

      globalLogger.warn('Auto-selection failed, using fallback', {
        operation: 'auto_select_location',
        error: err.message,
        availableCount: availableLocations.length,
      });

      // Fallback to All Locations
      currentLocationIdRef.current = null;
      setSelectedLocationState(null);
    }
  }, [profileId, profileRole, updateSentryLocationContext, setSelectedLocationState]);

  // Load locations from assignments; admins can see all org locations
  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      setLocationsListed(false);
      return;
    }

    if (!user || !profile || !activeOrgId) {
      setAllLocations([]);
      setSelectedLocationState(null);
      setIsLoading(false);
      setLocationsListed(false);
      return;
    }

    if (locationsQuery.isLoading) {
      setIsLoading(true);
      setLocationsListed(false);
      return;
    }

    if (locationsQuery.error) {
      const err = locationsQuery.error instanceof Error
        ? locationsQuery.error
        : new Error('Unknown error');

      // Check if this was a rate-limit (429) — common during org switches.
      // TanStack Query already exhausted retries, but locations will reload
      // on next navigation or refocus, so just log without alarming the user.
      const isRateLimit = 'status' in locationsQuery.error &&
        (locationsQuery.error as { status?: number }).status === 429;

      Sentry.captureException(locationsQuery.error, {
        tags: {
          component: 'LocationContext',
          operation: 'loadLocations',
          userId: user?.id,
          userEmail: user?.email,
        },
        extra: {
          profileLocations: profile?.locations?.length || 0,
          error: err.message,
          isRateLimit,
        },
        level: isRateLimit ? 'warning' : 'error',
      });

      globalLogger.error('Failed to load locations', {
        operation: 'load_locations',
        error: err.message,
        userId: user?.id,
        isRateLimit,
      });

      if (!isRateLimit) {
        toast({
          title: i18next.t('common:location.loadError'),
          description: i18next.t('common:location.loadErrorDescription'),
          variant: 'destructive',
        });
      }

      setAllLocations([]);
      setIsLoading(false);
      setLocationsListed(false);
      return;
    }

    if (locationsQuery.data) {
      // EVERY active location, delegated ("shadow") rows included. The
      // delegated split happens ONCE, in the `locations` memo above: a shadow
      // row is never picked, printed or held back (the push worker already
      // skips it), but it is still the place a stored order, transfer,
      // shipment or log row points at, so its NAME has to stay resolvable.
      const allAvailableLocations: Location[] = (locationsQuery.data ?? [])
        .filter((loc) => loc.is_active !== false)
        .map((loc) => ({
          id: loc.id,
          shopify_location_id: loc.shopify_location_id,
          name: loc.name,
          country_code: loc.country_code ?? '',
          timezone: loc.timezone ?? 'Australia/Melbourne',
          city: loc.city ?? '',
          is_active: loc.is_active ?? true,
          production_enabled:
            (loc as { production_enabled?: boolean | null }).production_enabled ?? true,
          enable_auto_availability: loc.enable_auto_availability ?? true,
          // BLU-367: 3PL provenance — propagate so consumers (badge UI,
          // location switcher group, packing queue) can render shared vs owned.
          owner_organization_id:
            (loc as { owner_organization_id?: string | null }).owner_organization_id ?? null,
          owner_organization_name:
            (loc as { owner_organization_name?: string | null }).owner_organization_name ?? null,
          is_shared: (loc as { is_shared?: boolean }).is_shared ?? false,
          client_stock_writes_enabled:
            (loc as { client_stock_writes_enabled?: boolean }).client_stock_writes_enabled ?? false,
          delegated_to_location_id:
            (loc as { delegated_to_location_id?: string | null }).delegated_to_location_id ?? null,
          queue_default_client_org_id:
            (loc as { queue_default_client_org_id?: string | null }).queue_default_client_org_id ?? null,
        }));

      // Auto-selection walks the SELECTABLE list: landing an operator on a
      // location whose fulfilment someone else runs is a dead end.
      const selectableLocations = excludeDelegatedLocations(allAvailableLocations);

      globalLogger.info('Locations loaded successfully', {
        operation: 'load_locations',
        count: selectableLocations.length,
        role: profile.role,
      });

      setAllLocations(allAvailableLocations);
      setIsLoading(false);
      setLocationsListed(true);

      if (selectableLocations.length > 0) {
        autoSelectLocation(selectableLocations);
      } else {
        globalLogger.warn('User has no locations assigned', {
          operation: 'load_locations',
          userId: user.id,
          role: profile.role,
        });
      }
    }
  }, [
    authLoading,
    user,
    profile,
    activeOrgId,
    autoSelectLocation,
    setSelectedLocationState,
    locationsQuery.data,
    locationsQuery.error,
    locationsQuery.isLoading,
  ]);

  // Clear selected location and saved localStorage immediately when org switch starts
  useEffect(() => {
    const onOrgChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ organizationId?: string }>).detail;
      const nextOrgId = detail?.organizationId;
      try {
        localStorage.removeItem(LOCATION_ID_STORAGE_KEY);
        localStorage.removeItem(LOCATION_NAME_STORAGE_KEY);
      } catch { /* ignore */ }
      if (nextOrgId) {
        setActiveOrgId(nextOrgId);
      } else {
        try {
          setActiveOrgId(
            localStorage.getItem(ACTIVE_ORG_ID_STORAGE_KEY) || localStorage.getItem(LEGACY_ORG_ID_STORAGE_KEY)
          );
        } catch {
          setActiveOrgId(null);
        }
      }
      currentLocationIdRef.current = null;
      setIsLoading(true);
      setLocationsListed(false);
      setAllLocations([]);
      setSelectedLocationState(null);
    };
    window.addEventListener('org-changed', onOrgChanged);
    return () => window.removeEventListener('org-changed', onOrgChanged);
  }, [setSelectedLocationState]);

  // Update selected location and save to localStorage
  const setSelectedLocation = (location: Location | null) => {
    if (location === null) {
      // "All Locations" selected — store sentinel so admin default doesn't override
      if (currentLocationIdRef.current === null) return;
      globalLogger.info('User switched to All Locations', {
        operation: 'set_location',
        userId: profile?.id,
      });
      currentLocationIdRef.current = null;
      setSelectedLocationState(null);
      localStorage.setItem(LOCATION_ID_STORAGE_KEY, ALL_LOCATIONS_SENTINEL);
      localStorage.removeItem(LOCATION_NAME_STORAGE_KEY);
      return;
    }

    // Already selected (perhaps as the automatic default): the pick still
    // makes it the operator's own choice, saved like any other.
    if (currentLocationIdRef.current === location.id) {
      if (!selection.byUser) {
        setSelectedLocationState(selection.location ?? location, true);
        localStorage.setItem(LOCATION_ID_STORAGE_KEY, location.id);
        localStorage.setItem(LOCATION_NAME_STORAGE_KEY, location.name);
      }
      return;
    }

    // Verify the user has access to this location
    const hasAccess = locations.some(loc => loc.id === location.id);
    if (!hasAccess) {
      globalLogger.error('User attempted to access unauthorized location', {
        operation: 'set_location',
        locationName: location.name,
        locationId: location.id,
        userId: profile?.id,
      });
      return;
    }

    globalLogger.info('User switched location', {
      operation: 'set_location',
      locationName: location.name,
      locationId: location.id,
      userId: profile?.id,
    });

    currentLocationIdRef.current = location.id;
    setSelectedLocationState(location, true);
    localStorage.setItem(LOCATION_ID_STORAGE_KEY, location.id);
    localStorage.setItem(LOCATION_NAME_STORAGE_KEY, location.name);
    updateSentryLocationContext(location);
  };

  const value: LocationContextType = {
    selectedLocation,
    selectedByUser: selection.byUser,
    setSelectedLocation,
    locations,
    allLocations,
    isLoading,
    locationsListed,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}; 
